import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, BookMarked, Trophy, Library, ClipboardList, TrendingUp, PencilLine, BookText, ClipboardCheck, Target, FolderOpen, CalendarDays } from "lucide-react";

const SEC_LABELS = {
  paragraf: "Paragraf", deyim_atasozu: "Deyim & Atasözü", ingilizce_kelime: "İng. Kelime",
  turkce_sozluk: "Türkçe Sözlük", kitap: "Kitap",
};

export default function Overview() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [studentName, setStudentName] = useState(params.get("name") || "");
  const [evalDoc, setEvalDoc] = useState(null);
  const [exams, setExams] = useState([]);
  const [books, setBooks] = useState([]);
  const [plan, setPlan] = useState(null);
  const [dq, setDq] = useState([]);
  const [progress, setProgress] = useState([]);
  const [sinav, setSinav] = useState([]);
  const [resAsg, setResAsg] = useState([]);
  const [calendar, setCalendar] = useState([]);

  useEffect(() => {
    api.get("/exam-calendar").then(({ data }) => setCalendar(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (studentId) return;
    api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); setStudentName(data.student_name || ""); });
  }, [studentId]);

  useEffect(() => {
    if (!studentId) return;
    api.get("/monthly-eval", { params: { student_id: studentId } }).then(({ data }) => setEvalDoc(data));
    api.get("/exams", { params: { student_id: studentId } }).then(({ data }) => setExams(data));
    api.get("/reading-books", { params: { student_id: studentId } }).then(({ data }) => setBooks(data?.books || []));
    api.get("/study-program", { params: { student_id: studentId } }).then(({ data }) => setPlan(data));
    api.get("/daily-questions", { params: { student_id: studentId } }).then(({ data }) => setDq(data));
    api.get("/unit-progress", { params: { student_id: studentId } }).then(({ data }) => setProgress(data));
    api.get("/sinav-takip", { params: { student_id: studentId } }).then(({ data }) => setSinav(data));
    api.get("/resources/assignments", { params: { student_id: studentId } }).then(({ data }) => setResAsg(data)).catch(() => {});
  }, [studentId]);

  if (!studentId) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500">Bağlı öğrenci bulunamadı.</div>;

  const sections = evalDoc?.sections || {};
  const sectionStats = Object.entries(SEC_LABELS).map(([key, label]) => {
    const rows = sections[key] || [];
    return { label, count: rows.length, approved: rows.filter((r) => r.approved).length, net: rows.reduce((s, r) => s + (r.net || 0), 0) };
  });
  const lastExam = exams[exams.length - 1];
  const approvedBooks = books.filter((b) => b.approved).length;
  const totalCells = plan?.weeks?.reduce((s, w) => s + w.rows.reduce((a, r) => a + Object.keys(r.cells || {}).length, 0), 0) || 0;
  const doneCells = plan?.weeks?.reduce((s, w) => s + w.rows.reduce((a, r) => a + Object.values(r.cells || {}).filter((c) => c.status !== "bekliyor").length, 0), 0) || 0;

  const dqTotal = dq.reduce((s, r) => s + (r.soru || 0), 0);
  const dqD = dq.reduce((s, r) => s + (r.dogru || 0), 0);
  const dqY = dq.reduce((s, r) => s + (r.yanlis || 0), 0);
  const dqB = dq.reduce((s, r) => s + (r.bos || 0), 0);
  const dqNet = dq.reduce((s, r) => s + (r.net || 0), 0);
  const ezberGecti = progress.filter((p) => p.status === "ezber_gecti").length;
  const ozetBitti = progress.filter((p) => p.status === "ozet_bitti").length;
  const beklemede = progress.length - ezberGecti - ozetBitti;
  const resSecTotal = resAsg.reduce((s, a) => s + (a.resource?.sections?.length || 0), 0);
  const resSecDone = resAsg.reduce((s, a) => s + (a.resource?.sections || []).filter((_, i) => a.progress?.[i]?.done).length, 0);
  const unitExams = progress.filter((p) => p.exam);
  const sinavNet = sinav.reduce((s, r) => s + (r.net || 0), 0);
  const sinavByPanel = Object.entries(sinav.reduce((m, r) => {
    const k = r.panel || "turkce";
    (m[k] = m[k] || { count: 0, net: 0 });
    m[k].count += 1; m[k].net += r.net || 0;
    return m;
  }, {}));
  const PANEL_LABELS = { turkce: "Türkçe", matematik: "Matematik", fen: "Fen Bilimleri", inkilap: "İnkılap Tarihi", ingilizce: "İngilizce", din: "Din Kültürü" };
  const todayStr = new Date().toISOString().slice(0, 10);
  const upcoming = calendar.filter((c) => c.date >= todayStr).slice(0, 5);
  const daysLeft = (d) => Math.max(0, Math.round((new Date(d + "T00:00:00") - new Date(todayStr + "T00:00:00")) / 86400000));

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><LayoutDashboard className="h-6 w-6" /></div>
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Genel Bakış</h1>
          <p className="text-slate-500">{studentName} — tüm takip sonuçlarının özeti.</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={Trophy} color="bg-purple-500" label="Son Zirve Net" value={lastExam ? lastExam.total_net?.toFixed(2) : "—"} sub={lastExam?.name} />
        <Stat icon={ClipboardList} color="bg-indigo-500" label="Çalışma Programı" value={`${doneCells}/${totalCells}`} sub="tamamlanan görev" />
        <Stat icon={Library} color="bg-emerald-500" label="Okuma Kitabı" value={`${approvedBooks}/${books.length || 30}`} sub="onaylı sınav" />
        <Stat icon={TrendingUp} color="bg-amber-500" label="Zirve Sayısı" value={exams.length} sub={lastExam ? `LGS Puanı: ${lastExam.lgs?.puan ?? "—"}` : ""} />
        <Stat icon={PencilLine} color="bg-sky-500" label="Günlük Soru" value={dqTotal} sub={`Net ${dqNet.toFixed(2)}`} testid="ov-stat-daily" />
        <Stat icon={BookText} color="bg-orange-500" label="Özet & Ezber" value={`${ezberGecti}/${progress.length}`} sub="ünite ezber geçti" testid="ov-stat-ozet" />
        <Stat icon={ClipboardCheck} color="bg-rose-500" label="Sınav Takip" value={sinavNet.toFixed(2)} sub={`${sinav.length} sınav · toplam net`} testid="ov-stat-sinav" />
        <Stat icon={Target} color="bg-teal-500" label="Ünite Sınavı" value={unitExams.length} sub="girilen ünite sınavı" testid="ov-stat-unit-exam" />
        <Stat icon={FolderOpen} color="bg-lime-600" label="Kaynak İlerlemesi" value={`${resSecDone}/${resSecTotal}`} sub={`${resAsg.length} atanan kaynak`} testid="ov-stat-kaynak" />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><ClipboardList className="h-5 w-5 text-indigo-500" /> Aylık Değerlendirme Özeti</h3>
          {evalDoc ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-xs uppercase text-slate-400"><th className="py-2 text-left">Bölüm</th><th className="py-2 text-center">Satır</th><th className="py-2 text-center">Onaylı</th><th className="py-2 text-right">Toplam Net</th></tr></thead>
              <tbody className="divide-y divide-slate-100">
                {sectionStats.map((s) => (
                  <tr key={s.label}>
                    <td className="py-2.5 font-semibold text-slate-800">{s.label}</td>
                    <td className="py-2.5 text-center text-slate-500">{s.count}</td>
                    <td className="py-2.5 text-center"><span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-bold text-emerald-700">{s.approved}</span></td>
                    <td className="py-2.5 text-right font-mono font-bold text-indigo-600">{s.net.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="py-6 text-center text-slate-400">Bu ay için değerlendirme girilmedi.</p>}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-slate-900"><BookMarked className="h-5 w-5 text-emerald-500" /> Son Zirve Sonuçları</h3>
          {exams.length === 0 ? <p className="py-6 text-center text-slate-400">Henüz zirve sonucu yok.</p> : (
            <div className="space-y-3">
              {exams.slice(-3).reverse().map((e) => (
                <div key={e.id} className="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                  <div>
                    <div className="font-semibold text-slate-800">{e.name}</div>
                    <div className="text-xs text-slate-400">{e.date}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl font-extrabold text-indigo-700">{e.total_net?.toFixed(2)}</div>
                    <div className="text-[11px] text-slate-400">LGS Puanı {e.lgs?.puan ?? "—"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><PencilLine className="h-5 w-5 text-sky-500" /> Günlük Soru</h3>
          {dq.length === 0 ? <p className="py-6 text-center text-slate-400">Henüz kayıt yok.</p> : (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5 text-xs font-semibold">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-700">Toplam {dqTotal} soru</span>
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">{dqD} D</span>
                <span className="rounded-full bg-rose-100 px-2.5 py-1 text-rose-700">{dqY} Y</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-500">{dqB} B</span>
                <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-indigo-700">Net {dqNet.toFixed(2)}</span>
              </div>
              <div className="space-y-1.5">
                {dq.slice(0, 5).map((r) => (
                  <div key={r.id} data-testid={`ov-dq-${r.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                    <span className="font-semibold text-slate-700">{r.subject || "—"}</span>
                    <span className="text-xs text-slate-400">{r.date}</span>
                    <span className="text-slate-600">{r.soru} soru · <b className="text-indigo-600">{Number((r.net || 0).toFixed(2))} net</b></span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><BookText className="h-5 w-5 text-orange-500" /> Özet &amp; Ezber</h3>
          {progress.length === 0 ? <p className="py-6 text-center text-slate-400">Henüz ünite takibi yok.</p> : (
            <>
              <div className="mb-3 flex flex-wrap gap-1.5 text-xs font-semibold">
                <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-700">Ezber Geçti {ezberGecti}</span>
                <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700">Özet Bitti {ozetBitti}</span>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-slate-500">Beklemede {beklemede}</span>
              </div>
              {unitExams.length > 0 && (
                <div className="space-y-1.5">
                  {unitExams.slice(0, 5).map((p) => (
                    <div key={p.id} data-testid={`ov-ue-${p.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                      <span className="font-semibold text-slate-700">{p.subject_name} · {p.unit}</span>
                      <span className="text-slate-600">{p.exam.dogru}D {p.exam.yanlis}Y {p.exam.bos}B · <b className="text-indigo-600">{Number(p.exam.net.toFixed(2))} net</b></span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><CalendarDays className="h-5 w-5 text-rose-500" /> Yaklaşan Sınavlar</h3>
          {upcoming.length === 0 ? <p className="py-6 text-center text-slate-400">Yaklaşan sınav yok.</p> : (
            <div className="space-y-1.5">
              {upcoming.map((c) => (
                <div key={c.id} data-testid={`ov-upcoming-${c.id}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-700">{c.name}{c.grade ? ` · ${c.grade}. Sınıf` : ""}</span>
                  <span className="text-slate-600">{new Date(c.date + "T00:00:00").toLocaleDateString("tr-TR", { day: "2-digit", month: "short" })} · <b className="text-rose-600">{daysLeft(c.date) === 0 ? "Bugün!" : `${daysLeft(c.date)} gün kaldı`}</b></span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><ClipboardCheck className="h-5 w-5 text-rose-500" /> Sınav Takip (4 &amp; 7)</h3>
          {sinav.length === 0 ? <p className="py-6 text-center text-slate-400">Henüz sınav kaydı yok.</p> : (
            <div className="space-y-1.5">
              {sinavByPanel.map(([pk, v]) => (
                <div key={pk} data-testid={`ov-sinav-${pk}`} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2 text-sm">
                  <span className="font-semibold text-slate-700">{PANEL_LABELS[pk] || pk}</span>
                  <span className="text-slate-600">{v.count} sınav · <b className="text-indigo-600">{v.net.toFixed(2)} net</b></span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, color, label, value, sub, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white ${color}`}><Icon className="h-6 w-6" /></div>
      <div className="min-w-0">
        <div className="truncate text-2xl font-extrabold text-slate-900">{value}</div>
        <div className="text-sm text-slate-500">{label}{sub ? ` · ${sub}` : ""}</div>
      </div>
    </div>
  );
}
